/**
 * Access-token claim decoding — pure, no React, no network.
 *
 * Mirrors `app.core.security._extract_authorities` /
 * `has_deployment_wide_authority` (apps/python-backend/app/core/security.py):
 * the same JWT claim layout (`authorities` | `roles` | `scope`, plus
 * `workspaceRoles` folded into `WORKSPACE_{id}_{ROLE}` strings) and the same
 * deployment-wide short-circuit (`"*"` or `"ADMIN"` in the flat authority
 * set). Used to gate deployment-scoped admin UI (e.g. the replay-source
 * registry, HEI-4021) client-side; the backend re-checks on every request
 * regardless — this only controls what the UI offers to click.
 *
 * Never decodes or trusts the token's signature — this is a UI-affordance
 * read of already-issued claims, not an auth boundary.
 */

export type AccessTokenClaims = Record<string, unknown>;

function base64UrlDecode(segment: string): string {
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  return atob(padded);
}

/** Decodes the JWT payload segment without verifying the signature. Returns
 * `null` for a missing/malformed token rather than throwing — callers treat
 * "cannot decode" the same as "no authorities". */
export function decodeAccessTokenClaims(
  token: string | null | undefined
): AccessTokenClaims | null {
  if (!token) {
    return null;
  }
  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }
  try {
    const json = base64UrlDecode(parts[1]);
    const parsed = JSON.parse(json) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as AccessTokenClaims) : null;
  } catch {
    return null;
  }
}

function foldWorkspaceRoles(workspaceRoles: unknown): string[] {
  if (!workspaceRoles || typeof workspaceRoles !== 'object') {
    return [];
  }
  const folded: string[] = [];
  for (const [workspaceId, roles] of Object.entries(workspaceRoles as Record<string, unknown>)) {
    const roleNames = typeof roles === 'string' ? [roles] : Array.isArray(roles) ? roles : [];
    for (const role of roleNames) {
      if (role) {
        folded.push(`WORKSPACE_${workspaceId}_${String(role)}`);
      }
    }
  }
  return folded;
}

/** Python-`or`-style "empty" check: `None`, `''`, and `[]` are all falsy in
 * the backend's `claims.get("authorities") or claims.get("roles") or
 * claims.get("scope") or []` chain, but only `[]` differs from JS's own
 * falsiness (an empty array is truthy in JS) — so this can't be a plain
 * `||` chain without also falling through on a present-but-empty
 * `authorities: []`, which is exactly the case the backend falls through
 * on too. */
function isEmptyClaimValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.length === 0;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
}

/** Same field-priority order as the backend: `authorities`, then `roles`,
 * then `scope`, plus `workspaceRoles` folded in — never merged from more
 * than one of the three top-level fields at once, and a present-but-empty
 * value at one priority falls through to the next (see
 * `isEmptyClaimValue`). */
export function extractAuthorities(claims: AccessTokenClaims | null): string[] {
  if (!claims) {
    return [];
  }
  const candidates = [claims.authorities, claims.roles, claims.scope];
  const raw = candidates.find(candidate => !isEmptyClaimValue(candidate)) ?? [];
  const flat: string[] = [];
  if (typeof raw === 'string') {
    flat.push(...raw.split(/[\s,]+/).filter(Boolean));
  } else if (Array.isArray(raw)) {
    flat.push(...raw.map(String));
  }
  return [...flat, ...foldWorkspaceRoles(claims.workspaceRoles)];
}

/** Deployment-wide ADMIN check — mirrors `require_deployment_admin`'s own
 * short-circuit. A per-workspace `WORKSPACE_{id}_ADMIN` authority (self-
 * service, granted to any workspace creator) never satisfies this. */
export function hasDeploymentWideAuthority(claims: AccessTokenClaims | null): boolean {
  const authorities = extractAuthorities(claims);
  return authorities.includes('*') || authorities.includes('ADMIN');
}

/** Workspace-scoped ADMIN check — mirrors `require_workspace_admin`'s source
 * of truth (JWT authorities, not the DB member-role table). Grants on a
 * deployment-wide authority (`*`/`ADMIN`, see `hasDeploymentWideAuthority`)
 * OR the specific `WORKSPACE_{id}_ADMIN` authority folded in by
 * `extractAuthorities` from the `workspaceRoles` claim (HEI-4037). This only
 * gates frontend affordances (which controls render); the backend
 * `require_workspace_admin` dependency re-checks on every request
 * regardless. */
export function hasWorkspaceAdminAuthority(
  claims: AccessTokenClaims | null,
  workspaceId: number
): boolean {
  const authorities = extractAuthorities(claims);
  return (
    authorities.includes('*') ||
    authorities.includes('ADMIN') ||
    authorities.includes(`WORKSPACE_${workspaceId}_ADMIN`)
  );
}

/**
 * Deployment-wide ADMIN check for gating deployment-scoped admin UI (e.g.
 * the replay-source registry, HEI-4021). Reads the access token directly
 * from `localStorage` rather than a hook (a non-reactive, call-at-render
 * convention) — the token only changes on login/logout, both of which navigate away from
 * whatever page called this. The backend re-checks on every request
 * regardless (`require_deployment_admin`); this only controls what the UI
 * offers to click.
 */
export const isDeploymentAdmin = (): boolean =>
  hasDeploymentWideAuthority(decodeAccessTokenClaims(localStorage.getItem('uvp_access_token')));

/**
 * Workspace-scoped ADMIN check for the same non-reactive, call-at-render
 * convention as `isDeploymentAdmin` above (HEI-4037, workspace authentication
 * integration self-service). Gates ADMIN-only mutation controls (bootstrap,
 * enable/promote, event list) on `WorkspaceAuthIntegrationCard`; the backend's
 * `require_workspace_admin` dependency remains the actual authority.
 */
export const isWorkspaceAdmin = (workspaceId: number): boolean =>
  hasWorkspaceAdminAuthority(
    decodeAccessTokenClaims(localStorage.getItem('uvp_access_token')),
    workspaceId
  );
